import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import type { WhoamiResponse } from '../api';
import {
  Button,
  Card,
  ErrorBanner,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { useWhoami } from '../lib/auth';

export function LoginPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const whoami = useWhoami();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState(''); // bootstrap flow
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const loginMut = useMutation({
    mutationFn: async () => {
      await api.post<WhoamiResponse>('/api/auth/login', { username, password });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['whoami'] });
      nav('/', { replace: true });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  const bootstrapMut = useMutation({
    mutationFn: async () => {
      if (password !== confirm) throw new Error('Passwords do not match.');
      await api.post<WhoamiResponse>('/api/auth/bootstrap', {
        username,
        password,
        pin: pin.trim() || null,
      });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['whoami'] });
      nav('/', { replace: true });
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : String(err)),
  });

  if (whoami.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-ink-muted">
        <div className="flex items-center gap-2">
          <Spinner /> loading…
        </div>
      </div>
    );
  }

  const isBootstrap = whoami.data?.state === 'bootstrap_required';

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg px-4 py-10 font-ui text-ink">
      <Card className="w-full max-w-sm">
        <h1 className="mb-1 text-body font-semibold text-ink">
          {isBootstrap ? 'Create owner account' : 'Sign in'}
        </h1>
        <p className="mb-4 text-caption text-ink-muted">
          {isBootstrap
            ? 'Set up the single owner for this installation. You can add boss (read-only) users later from Settings → Users.'
            : 'Accounts is a private, invite-only app. Your account was created by the owner.'}
        </p>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (isBootstrap) bootstrapMut.mutate();
            else loginMut.mutate();
          }}
        >
          <TextInput
            label="Username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            name="username"
          />
          <TextInput
            label={isBootstrap ? 'New password (≥ 10 chars)' : 'Password'}
            type="password"
            autoComplete={isBootstrap ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            name="password"
          />
          {isBootstrap && (
            <>
              <TextInput
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                name="confirm"
              />
              <TextInput
                label="PIN (optional — 4–64 chars)"
                hint="The PIN gates destructive actions. You can set or change it later."
                type="password"
                autoComplete="new-password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                name="pin"
              />
            </>
          )}
          {error && <ErrorBanner>{error}</ErrorBanner>}
          <Button
            type="submit"
            disabled={loginMut.isPending || bootstrapMut.isPending}
          >
            {loginMut.isPending || bootstrapMut.isPending ? (
              <Spinner />
            ) : isBootstrap ? (
              'Create owner'
            ) : (
              'Sign in'
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}
